import { useQuery } from '@atlassian/conversation-assistant-service/common/utils/use-query';
import { performGqlFetch } from '@atlassian/conversation-assistant-utils/graphqlUtils';

import { parseAriSafely } from '../../../../common/utils';

import { type ValidateImpactedWorkAriResult } from './useValidateImpactedWorkAri';

const TENANT_CONTEXTS_QUERY = `
	query useValidateJiraIssueAriTenantContexts($cloudIds: [ID!]!) {
		tenantContexts(cloudIds: $cloudIds) {
			cloudId
			orgId
		}
	}
`;

const USER_ACCESSIBLE_JIRA_SITES_QUERY = `
	query useValidateJiraIssueAriUserAccessibleJiraSites($cloudId: ID!) {
		mercury_jiraProvider @optIn(to: "Mercury") {
			userAccessibleConnectedJiraSites(cloudId: $cloudId) {
				edges {
					node {
						id
					}
				}
			}
		}
	}
`;

type TenantContextsResponse = {
	tenantContexts: { cloudId: string; orgId: string }[];
};

type UserAccessibleJiraSitesResponse = {
	mercury_jiraProvider: {
		userAccessibleConnectedJiraSites: {
			edges: { node: { id: string } }[];
		};
	};
};

/**
 * Validates a Jira issue ARI against the user's current Mercury workspace.
 * @param ari           - The raw ARI string. The hook parses it internally
 * @param currentCloudId - The cloudId of the current Mercury workspace.
 */
export const useValidateJiraIssueAri = (
	ari: string | null,
	currentCloudId: string,
): ValidateImpactedWorkAriResult => {
	const parsed = ari ? parseAriSafely(ari) : null;
	const ariCloudId = parsed?.resourceOwner === 'jira' ? (parsed.cloudId ?? null) : null;
	const queriesEnabled = !!ariCloudId && !!currentCloudId;

	const {
		data: orgs,
		isFetching: orgsLoading,
		error: orgsError,
	} = useQuery({
		queryKey: [
			'focus-ask-impacted-work-validate-jira-issue-orgs',
			currentCloudId,
			ariCloudId ?? '',
		],
		enabled: queriesEnabled,
		queryFn: () =>
			performGqlFetch<TenantContextsResponse, { cloudIds: string[] }>({
				gqlQuery: TENANT_CONTEXTS_QUERY,
				// The `ariCloudId` is guaranteed non-null here by the `enabled` flag above,
				// but TypeScript can't infer that — fall back to the workspace cloudId
				// as a safe no-op (the query is disabled, so the value is never used).
				variables: { cloudIds: [currentCloudId, ariCloudId ?? currentCloudId] },
			}),
	});

	const {
		data: sites,
		isFetching: sitesLoading,
		error: sitesError,
	} = useQuery({
		queryKey: [
			'focus-ask-impacted-work-validate-jira-issue-sites',
			currentCloudId,
			ariCloudId ?? '',
		],
		enabled: queriesEnabled,
		queryFn: () =>
			performGqlFetch<UserAccessibleJiraSitesResponse, { cloudId: string }>({
				gqlQuery: USER_ACCESSIBLE_JIRA_SITES_QUERY,
				variables: { cloudId: currentCloudId },
			}),
	});

	if (!ariCloudId) return { status: 'notFound' };
	if (orgsLoading || sitesLoading) return { status: 'loading' };
	if (queriesEnabled && orgs === undefined && !orgsError) return { status: 'loading' };
	if (queriesEnabled && sites === undefined && !sitesError) return { status: 'loading' };

	if (orgsError || sitesError) return { status: 'error' };

	const workspaceOrg = orgs?.data?.tenantContexts?.find((c) => c.cloudId === currentCloudId)?.orgId;
	const ariOrg = orgs?.data?.tenantContexts?.find((c) => c.cloudId === ariCloudId)?.orgId;
	if (!workspaceOrg || !ariOrg || workspaceOrg !== ariOrg) {
		return { status: 'notFound' };
	}

	const connectedCloudIds =
		sites?.data?.mercury_jiraProvider?.userAccessibleConnectedJiraSites?.edges
			?.map((edge) => parseAriSafely(edge.node.id)?.resourceId)
			.filter((id): id is string => Boolean(id)) ?? [];
	if (!connectedCloudIds.includes(ariCloudId)) {
		return { status: 'notFound' };
	}

	return { status: 'inWorkspace' };
};
